import { openDB } from 'idb'
import type { StoreDump } from './readAll'

/**
 * Durable recovery snapshots (D-030).
 *
 * Written to the Origin Private File System, falling back to a SEPARATE
 * IndexedDB database when OPFS is unavailable. Both sinks persist to disk and
 * survive a crash, a tab close, and a reload.
 *
 * An in-memory Blob is never a recovery artifact. If neither sink is available,
 * `writeSnapshot` fails rather than pretending to have stored something.
 */

export type SnapshotPurpose = 'pre-migration' | 'pre-restore'
export type SnapshotSink = 'opfs' | 'indexeddb'

export interface RecoverySnapshotRef {
  readonly id: string
  readonly purpose: SnapshotPurpose
  readonly createdAt: string
  readonly sink: SnapshotSink
  readonly byteSize: number
  readonly blobCount?: number
  readonly fromVersion?: number
  readonly toVersion?: number
}

export interface SnapshotBlob {
  readonly blobId: string
  readonly blob: Blob
  readonly mimeType: string
}

export interface SnapshotPayload {
  readonly purpose: SnapshotPurpose
  readonly records: StoreDump
  /**
   * Blob bytes, for pre-restore snapshots only.
   *
   * A pre-migration snapshot omits them because blob payloads are immutable
   * (D-029), so a migration cannot damage them and copying would double storage
   * for nothing. A RESTORE does replace blobs, so metadata alone would not be
   * recoverable (D-030).
   */
  readonly blobs?: readonly SnapshotBlob[]
  readonly fromVersion?: number
  readonly toVersion?: number
}

const RECOVERY_DB_NAME = 'project-sola-recovery'
const RECOVERY_STORE = 'snapshots'
const OPFS_DIRECTORY = 'recovery'

/**
 * Retention (D-030). Pre-migration snapshots survive until the next schema
 * migration or 30 days; pre-restore snapshots until the user confirms the
 * restore looks correct or 7 days.
 */
export const RETENTION_DAYS: Readonly<Record<SnapshotPurpose, number>> = {
  'pre-migration': 30,
  'pre-restore': 7,
}

function isOpfsAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.storage !== 'undefined' &&
    typeof navigator.storage.getDirectory === 'function'
  )
}

async function openRecoveryDb() {
  return openDB(RECOVERY_DB_NAME, 1, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(RECOVERY_STORE)) {
        database.createObjectStore(RECOVERY_STORE, { keyPath: 'id' })
      }
    },
  })
}

function snapshotId(purpose: SnapshotPurpose, createdAt: string): string {
  return `${purpose}-${createdAt.replace(/[:.]/g, '-')}`
}

/**
 * Write a snapshot durably.
 *
 * Serialization happens first and completely, outside any transaction, so the
 * IndexedDB fallback holds an open transaction only for the final put.
 */
export async function writeSnapshot(payload: SnapshotPayload): Promise<RecoverySnapshotRef> {
  const createdAt = new Date().toISOString()
  const id = snapshotId(payload.purpose, createdAt)
  const serialized = JSON.stringify({
    id,
    purpose: payload.purpose,
    createdAt,
    fromVersion: payload.fromVersion,
    toVersion: payload.toVersion,
    records: payload.records,
  })
  const byteSize = new TextEncoder().encode(serialized).byteLength

  const blobs = payload.blobs ?? []
  const blobBytes = blobs.reduce((sum, entry) => sum + entry.blob.size, 0)

  const base = {
    id,
    purpose: payload.purpose,
    createdAt,
    byteSize: byteSize + blobBytes,
    blobCount: blobs.length,
    ...(payload.fromVersion === undefined ? {} : { fromVersion: payload.fromVersion }),
    ...(payload.toVersion === undefined ? {} : { toVersion: payload.toVersion }),
  }

  if (isOpfsAvailable()) {
    try {
      const root = await navigator.storage.getDirectory()
      const directory = await root.getDirectoryHandle(OPFS_DIRECTORY, { create: true })
      const file = await directory.getFileHandle(`${id}.json`, { create: true })
      const writable = await file.createWritable()
      await writable.write(serialized)
      await writable.close()

      // Blobs go beside the records as individual files, one at a time, so no
      // more than one is resident while writing.
      if (blobs.length > 0) {
        const blobDirectory = await directory.getDirectoryHandle(`${id}-blobs`, { create: true })
        for (const entry of blobs) {
          const handle = await blobDirectory.getFileHandle(entry.blobId, { create: true })
          const blobWritable = await handle.createWritable()
          await blobWritable.write(entry.blob)
          await blobWritable.close()
        }
      }

      return { ...base, sink: 'opfs' }
    } catch {
      // Fall through to IndexedDB rather than losing the snapshot.
    }
  }

  const db = await openRecoveryDb()
  try {
    // IndexedDB stores Blob values natively, so the fallback keeps the bytes
    // durably too rather than degrading to metadata only.
    await db.put(RECOVERY_STORE, { ...base, sink: 'indexeddb', serialized, blobs })
    return { ...base, sink: 'indexeddb' }
  } finally {
    db.close()
  }
}

/** Snapshots from both sinks, newest first. */
export async function listSnapshots(): Promise<readonly RecoverySnapshotRef[]> {
  const refs = [...(await listOpfsSnapshots()), ...(await listIndexedDbSnapshots())]
  return refs.sort((left, right) => right.createdAt.localeCompare(left.createdAt))
}

async function listOpfsSnapshots(): Promise<readonly RecoverySnapshotRef[]> {
  if (!isOpfsAvailable()) return []

  const refs: RecoverySnapshotRef[] = []
  try {
    const root = await navigator.storage.getDirectory()
    const directory = await root.getDirectoryHandle(OPFS_DIRECTORY, { create: false })
    for await (const handle of directory.values()) {
      if (handle.kind !== 'file') continue
      const file = await handle.getFile()
      const ref = toRef(JSON.parse(await file.text()), 'opfs', file.size)
      if (ref) refs.push(ref)
    }
  } catch {
    // No OPFS recovery directory yet.
  }
  return refs
}

async function listIndexedDbSnapshots(): Promise<readonly RecoverySnapshotRef[]> {
  const refs: RecoverySnapshotRef[] = []
  try {
    const db = await openRecoveryDb()
    try {
      for (const row of await db.getAll(RECOVERY_STORE)) {
        const ref = toRef(row, 'indexeddb', (row as { byteSize?: number }).byteSize ?? 0)
        if (ref) refs.push(ref)
      }
    } finally {
      db.close()
    }
  } catch {
    // Recovery database not present.
  }
  return refs
}

export async function readSnapshot(id: string): Promise<SnapshotPayload | undefined> {
  if (isOpfsAvailable()) {
    try {
      const root = await navigator.storage.getDirectory()
      const directory = await root.getDirectoryHandle(OPFS_DIRECTORY, { create: false })
      const handle = await directory.getFileHandle(`${id}.json`, { create: false })
      const file = await handle.getFile()
      return JSON.parse(await file.text()) as SnapshotPayload
    } catch {
      // Try the fallback sink.
    }
  }

  try {
    const db = await openRecoveryDb()
    try {
      const row = await db.get(RECOVERY_STORE, id)
      const serialized = (row as { serialized?: string } | undefined)?.serialized
      return serialized === undefined ? undefined : (JSON.parse(serialized) as SnapshotPayload)
    } finally {
      db.close()
    }
  } catch {
    return undefined
  }
}

export async function deleteSnapshot(id: string): Promise<void> {
  if (isOpfsAvailable()) {
    try {
      const root = await navigator.storage.getDirectory()
      const directory = await root.getDirectoryHandle(OPFS_DIRECTORY, { create: false })
      await directory.removeEntry(`${id}.json`)
    } catch {
      // Not in OPFS; try the fallback.
    }
  }

  try {
    const db = await openRecoveryDb()
    try {
      await db.delete(RECOVERY_STORE, id)
    } finally {
      db.close()
    }
  } catch {
    // Nothing to remove.
  }
}

/** Remove snapshots past their retention window (D-030). */
export async function pruneExpiredSnapshots(now: Date): Promise<number> {
  const snapshots = await listSnapshots()
  let removed = 0

  for (const snapshot of snapshots) {
    const ageDays = (now.getTime() - Date.parse(snapshot.createdAt)) / 86_400_000
    if (ageDays > RETENTION_DAYS[snapshot.purpose]) {
      await deleteSnapshot(snapshot.id)
      removed += 1
    }
  }

  return removed
}

function toRef(
  value: unknown,
  sink: SnapshotSink,
  byteSize: number,
): RecoverySnapshotRef | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const row = value as Record<string, unknown>
  if (typeof row.id !== 'string' || typeof row.createdAt !== 'string') return undefined
  if (row.purpose !== 'pre-migration' && row.purpose !== 'pre-restore') return undefined

  return {
    id: row.id,
    purpose: row.purpose,
    createdAt: row.createdAt,
    sink,
    byteSize,
    ...(typeof row.fromVersion === 'number' ? { fromVersion: row.fromVersion } : {}),
    ...(typeof row.toVersion === 'number' ? { toVersion: row.toVersion } : {}),
  }
}
